import { create } from "zustand";
import { readInitialTheme, type Theme } from "./theme.js";
import { readInitialFixture } from "./fixture.js";
import { DEFAULT_ZOOM_IDX, clampZoomIdx } from "../lib/zoom.js";
import type { BentoModule, ModuleControls, PinnedMatch } from "./module.js";
import type { CompetitionSeason } from "@bento/shared";

/** What the selector opens on: Premier League 2026/27 (the current season). */
export const DEFAULT_COMP_SEASON = { competitionId: 2, seasonId: 351 } as const;

/** The season to use after switching to `competitionId`: its live (current)
 *  season — Bento only offers upcoming matches, so there's no season picker
 *  (CLAUDE.md "What Bento is for") — else the same season label, else its first. */
function pickSeason(
  state: { competitions: CompetitionSeason[]; competitionId: number; seasonId: number },
  competitionId: number
): number {
  const current = state.competitions.find(
    (c) => c.competitionId === state.competitionId && c.seasonId === state.seasonId
  );
  const options = state.competitions.filter((c) => c.competitionId === competitionId);
  const pick =
    options.find((c) => c.live) ??
    options.find((c) => c.season === current?.season) ??
    options[0];
  return pick?.seasonId ?? state.seasonId;
}

// The single source of truth the app reads from — the "brain" that replaces the
// legacy DOM-as-state pattern (M1 Phase 2). It grows one slice per PR.
export interface BentoState {
  // --- Theme slice ---
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;

  // --- Fixture slice ---
  // The current match everything is scoped to. Mirrored from the legacy fixture
  // <select>; `saveState`/undo already reads it from here.
  fixture: string;
  setFixture: (fixture: string) => void;
  // Label → StatsBomb match id for every fixture listed this session (BenBet
  // W1). The match id is what app-api keys a fixture by (unique across seasons);
  // the label stays the store's `fixture` because the legacy layer splits it for
  // team names. Filled by <FixturesSync> from GET /fixtures; empty until then.
  fixtureIds: Record<string, string>;
  // Label → kick-off (ISO UTC) for the same fixtures — what a pin needs to know
  // when to come off. Both maps are MERGED, not replaced, on each list load: a
  // pin survives a league switch (Ben, Q-006), so its match must stay resolvable.
  fixtureKickoffs: Record<string, string>;
  setFixtureIds: (
    fixtures: readonly { label: string; id: string; date?: string; kickOff?: string }[]
  ) => void;
  // The league-season the selector is scoped to (BenBet W1 PR 3b). Starts at
  // DEFAULT_COMP_SEASON (PL 26/27) so its fixture list can load in parallel with
  // GET /competitions instead of waiting for it. `competitions` = what app-api
  // serves, filled by <FixturesSync>; the select helpers pick a sensible season.
  competitions: CompetitionSeason[];
  setCompetitions: (competitions: CompetitionSeason[]) => void;
  competitionId: number;
  seasonId: number;
  /** League dropdown changed: use that league's live season (see pickSeason)
   *  and unpin every module (a pin doesn't cross leagues). */
  selectLeague: (competitionId: number) => void;
  selectSeason: (seasonId: number) => void;
  /** Set both at once — undo restores a snapshot's league-season with it. */
  setCompetitionSeason: (competitionId: number, seasonId: number) => void;
  /** GET /fixtures failed: widgets show an error instead of waiting forever. */
  fixturesError: boolean;
  setFixturesError: (failed: boolean) => void;

  // --- Zoom slice ---
  // Writes-only mirror of the zoom widget: legacy setZoom updates this. The
  // hot-path `currentScale` reads (drag/resize math) flip to the store later,
  // together with the drag/resize migration (they share that coupling).
  zoomIdx: number;
  setZoomIdx: (idx: number) => void;

  // --- Modules slice ---
  // The store's model of the placed modules on the canvas. Writes-only for now:
  // the legacy DOM still renders and is the source of truth; the store mirrors
  // every desktop mutation (create/close/clear + move/resize/control/pin) so
  // later slices can flip readers (saveState/undo, collision, presets) over to
  // it. (Mobile reorder position-sync is still pending — the hardest part.)
  modules: BentoModule[];
  // Replace the whole modules array in one shot — the hydration path for a
  // saved board (WS-D D5). `<Workspace>` renders each `<PlacedModule>` from the
  // store (self-wiring drag/resize/controls), so setting the array IS the load.
  // Distinct from addModule (single upsert, the legacy createModule path).
  setModules: (modules: BentoModule[]) => void;
  addModule: (module: BentoModule) => void;
  // Insert at a specific array index (M1 Phase 4, PR 10). The modules array
  // order IS the mobile single-column stack order, so mobile prepend (index 0)
  // and tap-to-place (index k) insert here; desktop still uses addModule (push).
  addModuleAt: (module: BentoModule, index: number) => void;
  removeModule: (id: string) => void;
  clearModules: () => void;
  moveModule: (id: string, col: number, row: number) => void;
  resizeModule: (id: string, cols: number, rows: number) => void;
  // Move a module to a new array index — the mobile drag-handle reorder commits
  // here on drop (PR 10; the deferred mobile reorder position-sync).
  reorderModule: (id: string, toIndex: number) => void;
  // Re-order the array by canvas position (row-major, matching the legacy
  // sortModulesForMobile) — the desktop→mobile switch (PR 10).
  sortModulesByPosition: () => void;
  // Pin / unpin. Pinning freezes the module on the match open now
  // (`BentoModule.pinnedMatch`, saved with the board); read it through
  // `selectModuleFixture`, so the widget AND its controls use the same match.
  setModulePinned: (id: string, pinned: boolean) => void;
  /** Undo puts a pinned module's match back (its snapshot recorded it); a no-op
   *  for a module that isn't pinned. */
  setPinnedMatch: (id: string, match: PinnedMatch) => void;
  /** Quietly unpin every module whose match has kicked off (Ben, Q-006: no
   *  message). Run on a timer by <PinExpiry>; board loads do it too. */
  unpinKickedOff: () => void;
  setModuleControls: (id: string, controls: Partial<ModuleControls>) => void;
  // Mobile arrangement (live-reflow drag): the new order + each module's
  // "pairs with the next one" flag, in one update.
  arrangeMobile: (list: { id: string; pairNext: boolean }[]) => void;
  // Mobile height (1.25–3.75 half-widths) for one module or a pair.
  setMobileUnits: (ids: string[], units: number) => void;

  // --- Canvas UI slice ---
  // The module whose resize handles + hover outline are showing (desktop). Set
  // by the legacy pointermove hit-testing in main.js (sticky dominance, so two
  // modules whose handle zones overlap don't flicker); <PlacedModule> reads it
  // to add `.resize-active`. Null = none.
  resizeActiveId: string | null;
  setResizeActiveId: (id: string | null) => void;
  // Marquee multi-select (desktop): the modules inside the drawn selection box.
  // <PlacedModule> renders `.module-selected`; main.js drags them as a group and
  // bulk-deletes them on Delete/Backspace.
  selectedIds: string[];
  setSelectedIds: (ids: string[]) => void;
  // Modules playing their close animation because of a bulk delete. Each
  // <PlacedModule> in here animates out, then removes itself (as a single close).
  closingIds: string[];
  closeModules: (ids: string[]) => void;
}

export const useBentoStore = create<BentoState>((set) => ({
  theme: readInitialTheme(),
  setTheme: (theme) => set({ theme }),
  toggleTheme: () =>
    set((state) => ({ theme: state.theme === "light" ? "dark" : "light" })),

  fixture: readInitialFixture(),
  setFixture: (fixture) => set({ fixture }),
  fixtureIds: {},
  fixtureKickoffs: {},
  setFixtureIds: (fixtures) =>
    set((state) => ({
      fixtureIds: { ...state.fixtureIds, ...Object.fromEntries(fixtures.map((f) => [f.label, f.id])) },
      fixtureKickoffs: {
        ...state.fixtureKickoffs,
        ...Object.fromEntries(
          fixtures.flatMap((f) => {
            const at = kickoffAt(f.date, f.kickOff);
            return at ? [[f.label, at]] : [];
          })
        ),
      },
    })),
  competitions: [],
  setCompetitions: (competitions) => set({ competitions }),
  competitionId: DEFAULT_COMP_SEASON.competitionId,
  seasonId: DEFAULT_COMP_SEASON.seasonId,
  selectLeague: (competitionId) =>
    // Pins stay (Ben, Q-006): "I should be able to look at a Championship
    // fixture whilst still having a Premier League game pinned."
    set((state) => ({ competitionId, seasonId: pickSeason(state, competitionId) })),
  selectSeason: (seasonId) => set({ seasonId }),
  setCompetitionSeason: (competitionId, seasonId) => set({ competitionId, seasonId }),
  fixturesError: false,
  setFixturesError: (fixturesError) => set({ fixturesError }),

  zoomIdx: DEFAULT_ZOOM_IDX,
  setZoomIdx: (idx) => set({ zoomIdx: clampZoomIdx(idx) }),

  modules: [],
  setPinnedMatch: (id, match) =>
    set((state) =>
      state.modules.some((m) => m.id === id && m.pinned)
        ? {
            modules: state.modules.map((m) => (m.id === id ? { ...m, pinnedMatch: match } : m)),
            ...knownIds(state, [match]),
          }
        : {}
    ),
  unpinKickedOff: () =>
    set((state) => {
      const now = Date.now();
      if (!state.modules.some((m) => m.pinned && hasKickedOff(m.pinnedMatch, now))) return {};
      return { modules: state.modules.map((m) => expirePin(m, now)) };
    }),
  setModules: (modules) =>
    set((state) => {
      // A saved board's pins come back on their own match — unless it has
      // kicked off since (then quietly unpinned).
      const next = normalisePins(state, modules, true);
      return { modules: next, ...knownIds(state, pinnedMatches(next)), selectedIds: [], closingIds: [] };
    }),
  addModule: (module) =>
    set((state) => {
      // Upsert by id so a re-add can't create a duplicate.
      const modules = normalisePins(state, [...state.modules.filter((m) => m.id !== module.id), module]);
      return { modules };
    }),
  addModuleAt: (module, index) =>
    set((state) => {
      const rest = state.modules.filter((m) => m.id !== module.id);
      const at = Math.max(0, Math.min(index, rest.length));
      return { modules: normalisePins(state, [...rest.slice(0, at), module, ...rest.slice(at)]) };
    }),
  reorderModule: (id, toIndex) =>
    set((state) => {
      const from = state.modules.findIndex((m) => m.id === id);
      if (from === -1) return {};
      const next = state.modules.slice();
      const moved = next.splice(from, 1)[0]!; // guarded by from !== -1
      const at = Math.max(0, Math.min(toIndex, next.length));
      next.splice(at, 0, moved);
      return { modules: next };
    }),
  sortModulesByPosition: () =>
    set((state) => ({
      // Row-major, with a small row-tolerance band, matching the legacy
      // sortModulesForMobile comparator (top, then left).
      modules: state.modules
        .slice()
        .sort((a, b) => (a.row === b.row ? a.col - b.col : a.row - b.row)),
    })),
  removeModule: (id) =>
    set((state) => ({
      modules: state.modules.filter((m) => m.id !== id),
      selectedIds: state.selectedIds.filter((x) => x !== id),
      closingIds: state.closingIds.filter((x) => x !== id),
    })),
  clearModules: () => set({ modules: [], selectedIds: [], closingIds: [] }),
  moveModule: (id, col, row) =>
    set((state) => ({
      modules: state.modules.map((m) => (m.id === id ? { ...m, col, row } : m)),
    })),
  resizeModule: (id, cols, rows) =>
    set((state) => ({
      modules: state.modules.map((m) => (m.id === id ? { ...m, cols, rows } : m)),
    })),
  setModulePinned: (id, pinned) =>
    set((state) => ({
      modules: state.modules.map((m) => {
        if (m.id !== id) return m;
        // Pinning freezes on the match open NOW (a fresh pin replaces an old one).
        if (pinned) return { ...m, pinned, pinnedMatch: matchOf(state, state.fixture) };
        return withoutPin(m);
      }),
    })),
  setMobileUnits: (ids, units) =>
    set((state) => ({
      modules: state.modules.map((m) => (ids.includes(m.id) ? { ...m, mobileUnits: units } : m)),
    })),
  arrangeMobile: (list) =>
    set((state) => {
      const byId = new Map(state.modules.map((m) => [m.id, m]));
      const next: BentoModule[] = [];
      list.forEach(({ id, pairNext }) => {
        const m = byId.get(id);
        if (m) next.push({ ...m, mobilePairNext: pairNext });
      });
      // Keep any module the list didn't mention (defensive), at the end.
      state.modules.forEach((m) => {
        if (!list.some((l) => l.id === m.id)) next.push(m);
      });
      return { modules: next };
    }),
  setModuleControls: (id, controls) =>
    set((state) => ({
      modules: state.modules.map((m) =>
        m.id === id ? { ...m, controls: { ...m.controls, ...controls } } : m
      ),
    })),

  resizeActiveId: null,
  setResizeActiveId: (id) => set({ resizeActiveId: id }),

  selectedIds: [],
  setSelectedIds: (ids) => set({ selectedIds: ids }),
  closingIds: [],
  closeModules: (ids) =>
    set((state) => ({
      closingIds: [...new Set([...state.closingIds, ...ids])],
      selectedIds: state.selectedIds.filter((x) => !ids.includes(x)),
    })),
}));

// ── Pins (Ben, Q-006) ────────────────────────────────────────────────────────

/** A fixture's kick-off as an ISO UTC instant. StatsBomb kick-offs are UTC; an
 *  unknown time counts from the start of the match day — never later, so a pin
 *  can't outlive its match ("never show pins from past matches"). */
export function kickoffAt(date?: string, kickOff?: string): string | null {
  if (!date) return null;
  return `${date}T${kickOff ? kickOff.slice(0, 8) : "00:00:00"}Z`;
}

function hasKickedOff(match: PinnedMatch | undefined, now: number): boolean {
  return match?.kickoffAt != null && Date.parse(match.kickoffAt) <= now;
}

/** The current state's record of a match: id + kick-off, as far as it knows. */
function matchOf(state: Pick<BentoState, "fixtureIds" | "fixtureKickoffs">, label: string): PinnedMatch {
  return {
    label,
    id: state.fixtureIds[label] ?? null,
    kickoffAt: state.fixtureKickoffs[label] ?? null,
  };
}

/** An unpinned module carries no match; a kicked-off pin comes off. */
function expirePin(m: BentoModule, now: number): BentoModule {
  if (m.pinned && !hasKickedOff(m.pinnedMatch, now)) return m;
  if (!m.pinned && m.pinnedMatch === undefined) return m;
  return withoutPin(m);
}

/** The module unpinned, with no match recorded. */
function withoutPin(m: BentoModule): BentoModule {
  const next = { ...m, pinned: false };
  delete next.pinnedMatch;
  return next;
}

/** Every pinned module has a match (a pin saved before matches were recorded,
 *  or re-created by undo, takes the match open now); unpinned modules carry
 *  none. `expire`: also unpin any whose match has kicked off (a board load). */
function normalisePins(
  state: Pick<BentoState, "fixture" | "fixtureIds" | "fixtureKickoffs">,
  modules: BentoModule[],
  expire = false
): BentoModule[] {
  const now = Date.now();
  return modules.map((m) => {
    if (m.pinned && !m.pinnedMatch) m = { ...m, pinnedMatch: matchOf(state, state.fixture) };
    if (!m.pinned && m.pinnedMatch === undefined) return m;
    if (!m.pinned || expire) return expirePin(m, now);
    return m;
  });
}

function pinnedMatches(modules: readonly BentoModule[]): PinnedMatch[] {
  return modules.flatMap((m) => (m.pinned && m.pinnedMatch ? [m.pinnedMatch] : []));
}

/** Teach the label → id / kick-off maps a pinned match's own record, so a pin
 *  from a league whose list isn't loaded (a reopened board) still fetches. */
function knownIds(
  state: Pick<BentoState, "fixtureIds" | "fixtureKickoffs">,
  matches: readonly PinnedMatch[]
): Partial<BentoState> {
  const ids = matches.filter((p) => p.id && !(p.label in state.fixtureIds));
  const kos = matches.filter((p) => p.kickoffAt && !(p.label in state.fixtureKickoffs));
  if (!ids.length && !kos.length) return {};
  return {
    fixtureIds: { ...state.fixtureIds, ...Object.fromEntries(ids.map((p) => [p.label, p.id!])) },
    fixtureKickoffs: {
      ...state.fixtureKickoffs,
      ...Object.fromEntries(kos.map((p) => [p.label, p.kickoffAt!])),
    },
  };
}

/** The match a module shows: its pinned match while pinned, else the live one. */
export function selectModuleFixture(
  state: Pick<BentoState, "fixture" | "modules">,
  moduleId: string
): string {
  const m = state.modules.find((x) => x.id === moduleId);
  return m?.pinned && m.pinnedMatch ? m.pinnedMatch.label : state.fixture;
}
